import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { listSessionNotifications, markCanonicalViewed } from '../api/canonicalSessionApi';
import { toDrawerItems } from '../chargerUx/notices';
import { onResume } from '../platform/lifecycle';

/**
 * useUnreadNotifications — inbox contract.
 *
 * The inbox is the in-app session notices the API records for the signed-in
 * user's sessions (charger UX). When the API does not offer them (its
 * charger-UX mode is off) the endpoint answers "not available" and the list
 * is simply empty, so the drawer keeps its "You're all caught up" state.
 *
 * One shared store behind the hook: the drawer, the sidebar badge and the
 * mobile top bar all mount it, and they must agree and fetch once. It is
 * refreshed on mount, on demand, and whenever the app returns to the
 * foreground — a notice missed while the app was closed is there on reopen.
 *
 * @returns {{
 *   unread: number,
 *   hasUnread: boolean,
 *   isLoading: boolean,
 *   error: Error | null,
 *   notifications: Array<{
 *     id: string | number,
 *     title: string,
 *     body?: string,
 *     timestamp?: string,
 *     unread?: boolean,
 *     href?: string,
 *     tone?: 'success' | 'info' | 'warning' | 'danger' | 'neutral',
 *   }>,
 *   markAsRead: (id: string | number) => void,
 *   markAllAsRead: () => void,
 *   refresh: () => void,
 * }}
 */

let store = { notifications: [], isLoading: false, error: null, loaded: false };
const listeners = new Set();
let inFlight = null;

const set = (patch) => {
  store = { ...store, ...patch };
  for (const l of listeners) l();
};
const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };
const read = () => store;

function load() {
  if (inFlight) return inFlight;
  set({ isLoading: !store.loaded });
  inFlight = listSessionNotifications()
    .then((result) => {
      if (result.ok) set({ notifications: toDrawerItems(result.notifications), error: null, isLoading: false, loaded: true });
      // Signed out, or a transient failure: keep what is shown.
      else set({ isLoading: false, loaded: true, error: result.status === 401 ? null : new Error(result.error || 'NOTIFICATIONS_UNAVAILABLE') });
    })
    .catch((err) => set({ isLoading: false, loaded: true, error: err }))
    .finally(() => { inFlight = null; });
  return inFlight;
}

function acknowledge(items) {
  // Reading a notice means the session's state has been seen up to that
  // version; the API clears it (and anything older) for every device.
  const latest = new Map();
  for (const item of items) {
    if (!item.sessionUid) continue;
    latest.set(item.sessionUid, Math.max(latest.get(item.sessionUid) ?? 0, item.sessionVersion ?? 0));
  }
  for (const [sessionUid, version] of latest) markCanonicalViewed(sessionUid, version || null);
}

export function useUnreadNotifications() {
  const { notifications, isLoading, error, loaded } = useSyncExternalStore(subscribe, read, read);

  useEffect(() => {
    if (!loaded) load();
  }, [loaded]);

  useEffect(() => {
    let off = null;
    let cancelled = false;
    Promise.resolve(onResume(() => { load(); })).then((unsubscribe) => {
      if (cancelled) { if (typeof unsubscribe === 'function') unsubscribe(); } else off = unsubscribe;
    }).catch(() => {});
    return () => { cancelled = true; if (typeof off === 'function') off(); };
  }, []);

  const markAsRead = useCallback((id) => {
    if (id == null) return;
    const target = store.notifications.filter((n) => n.id === id && n.unread);
    if (!target.length) return;
    set({ notifications: store.notifications.map((n) => (n.id === id ? { ...n, unread: false } : n)) });
    acknowledge(target);
  }, []);

  const markAllAsRead = useCallback(() => {
    const target = store.notifications.filter((n) => n.unread);
    if (!target.length) return;
    set({ notifications: store.notifications.map((n) => ({ ...n, unread: false })) });
    acknowledge(target);
  }, []);

  const refresh = useCallback(() => { load(); }, []);

  const unread = useMemo(
    () => notifications.reduce((n, item) => n + (item.unread ? 1 : 0), 0),
    [notifications],
  );

  return useMemo(() => ({
    unread,
    hasUnread: unread > 0,
    isLoading,
    error,
    notifications,
    markAsRead,
    markAllAsRead,
    refresh,
  }), [unread, isLoading, error, notifications, markAsRead, markAllAsRead, refresh]);
}

/** Tests only. */
export function _resetNotificationsStore() {
  store = { notifications: [], isLoading: false, error: null, loaded: false };
  inFlight = null;
  listeners.clear();
}
